/* SETTINGS — six windows in one stretched grid: who you are, what you are aiming
   at, what units, which tape fields, where the data comes from, and how to take
   it all away with you.

   Three of the six are backed by real server state (`/goals`, `/goals` again,
   `/admin/sync/status`), one by the auth session, one by `/export.csv`, and one —
   UNITS.CFG — by nothing at all, because v1 is locked to lb and inches. The
   design draws all six identically; the difference between "a control" and "a
   statement of fact" is made in the markup: UNITS renders spans, not buttons.

   Reads are per-panel and independent, as on Log: a failed `/goals` must not
   blank the export button. */
import { useCallback, useEffect, useRef, useState } from 'react'
import { Alert, Button, Checkbox, Input, Pill, Window, type PillColor } from '../ds'
import {
  ApiError,
  errorMessage,
  fetchExportCsv,
  getGoals,
  getSyncStatus,
  postFatSecretLinkStart,
  postGoals,
  postSyncNow,
  type ExportTable,
  type SyncStatus,
} from '../api'
import { useAuth } from '../auth'
import '../styles/settings.css'

/* ── Goal keys ───────────────────────────────────────────────────────────── */

/* `/goals` is a flat map of STRINGS, shared with the MCP `set_goal` tool and read
   by the coach on every call. So the keys are a wire contract with something
   outside this repo, and the names are the ones already in use: `target_weight`
   is written by the seed data, the backend tests and the tool's own docstring.
   `weekly_rate` and `log_measurements` are new here and follow the same shape. */

export const GOAL_TARGET_WEIGHT = 'target_weight'
export const GOAL_WEEKLY_RATE = 'weekly_rate'

/** Which tape fields LOG should show. Comma-separated metric names. */
export const GOAL_LOG_MEASUREMENTS = 'log_measurements'

const MEASUREMENT_FIELDS: readonly { metric: string; label: string }[] = [
  { metric: 'waist', label: 'Waist' },
  { metric: 'hips', label: 'Hips' },
  { metric: 'chest', label: 'Chest' },
  { metric: 'arms', label: 'Arms' },
  { metric: 'thighs', label: 'Thighs' },
]

const ALL_MEASUREMENTS = MEASUREMENT_FIELDS.map((f) => f.metric)

/**
 * Which tape fields are shown, from the stored preference.
 *
 * `undefined` (never set) and `''` (set to nothing) are different answers and
 * must not collapse into one: unset means "the default, all five", while an
 * empty string is a user who deliberately unticked every box. Treating the
 * second as the first would silently re-tick them on every reload.
 */
export function parseMeasurementPref(raw: string | undefined): Set<string> {
  if (raw === undefined) return new Set(ALL_MEASUREMENTS)
  const picked = raw
    .split(',')
    .map((part) => part.trim().toLowerCase())
    .filter(Boolean)
  // Unknown names are dropped rather than kept: the value is writable over MCP,
  // and a typo there should not become a checkbox nobody can see.
  return new Set(picked.filter((name) => ALL_MEASUREMENTS.includes(name)))
}

/** The stored form. Always in the screen's own order, so the value is stable. */
export function formatMeasurementPref(shown: ReadonlySet<string>): string {
  return ALL_MEASUREMENTS.filter((name) => shown.has(name)).join(',')
}

/* ── Sync status ─────────────────────────────────────────────────────────── */

export interface PillSpec {
  color: PillColor
  label: string
}

function twoDigit(n: number): string {
  return String(n).padStart(2, '0')
}

const MONTHS = [
  'JAN',
  'FEB',
  'MAR',
  'APR',
  'MAY',
  'JUN',
  'JUL',
  'AUG',
  'SEP',
  'OCT',
  'NOV',
  'DEC',
]

function sameLocalDay(a: Date, b: Date): boolean {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  )
}

/**
 * The FATSECRET pill, from real `/admin/sync/status`.
 *
 * The prototype prints a flat mint `SYNCED 03:12`, which is only true for one of
 * five states this endpoint can be in. Each of the other four is reported as
 * itself, in paper rather than mint, because mint means "positive" in this
 * palette and "we never configured FatSecret" is not positive news.
 *
 * A bare `HH:MM` is also only honest on the day it happened: at 09:00 on Tuesday
 * a pill reading `SYNCED 03:12` claims six hours ago whether the sync was this
 * morning or last March. So the time is shown for today's syncs and the date for
 * anything older.
 */
export function fatsecretPill(sync: SyncStatus, now: Date): PillSpec {
  if (!sync.consumer_configured) return { color: 'paper', label: 'NOT CONFIGURED' }
  if (!sync.linked) return { color: 'paper', label: 'NOT LINKED' }
  if (!sync.last_fatsecret_sync) return { color: 'paper', label: 'LINKED / NEVER SYNCED' }

  const at = new Date(sync.last_fatsecret_sync)
  // A timestamp the browser cannot parse is a backend bug, not a reason to crash
  // the panel or to print "Invalid Date" inside a pill.
  if (Number.isNaN(at.getTime())) return { color: 'mint', label: 'SYNCED' }

  const when = sameLocalDay(at, now)
    ? `${twoDigit(at.getHours())}:${twoDigit(at.getMinutes())}`
    : `${MONTHS[at.getMonth()]} ${twoDigit(at.getDate())}`
  return { color: 'mint', label: `SYNCED ${when}` }
}

/* ── Sync now ────────────────────────────────────────────────────────────── */

/**
 * The "Sync now" button's own state machine, independent of the `SyncStatus` read
 * that drives the pill above it. `POST /admin/sync/now` can answer five different
 * ways (see `postSyncNow` in `api.ts`), and `started`/`sync_in_progress` both hand off
 * to polling `GET /admin/sync/status` rather than resolving immediately — so this has
 * more states than the request itself has responses.
 *
 * `idle` renders nothing: a screen that has never had the button pressed has nothing
 * to report about it, unlike EXPORT.CFG's line which always states the table count.
 */
export type SyncNowState =
  | { phase: 'idle' }
  | { phase: 'requesting' }
  | { phase: 'recently_synced' }
  | { phase: 'polling' }
  | { phase: 'synced' }
  | { phase: 'sync_failed'; error: string }
  | { phase: 'relink_required' }
  | { phase: 'unconfigured' }
  | { phase: 'poll_timeout' }
  | { phase: 'error'; message: string }

export interface SyncNowLine {
  tone: 'good' | 'bad'
  text: string
}

/**
 * One sentence for the status line under "Sync now" — the whole point being that
 * every phase gets ITS OWN sentence. A button that shows a spinner and then nothing
 * for `recently_synced`, or that claims success for `relink_required`, is worse than
 * no button: the person who pressed it is left guessing whether anything happened.
 *
 * `relink_required` gets a sentence that tells the user what to do, not just what
 * went wrong. It used to point at `docs/fatsecret-setup.md` and a terminal, because
 * there was no connect/reconnect flow in the UI; there is one now, sitting a few pixels
 * below this line, so it points there instead.
 */
export function syncNowLine(state: SyncNowState): SyncNowLine | null {
  switch (state.phase) {
    case 'idle':
      return null
    case 'requesting':
      return { tone: 'good', text: 'SYNCING…' }
    case 'polling':
      return { tone: 'good', text: 'SYNC RUNNING ✦ CHECKING BACK EVERY FEW SECONDS…' }
    case 'recently_synced':
      // Debounced, not skipped: FatSecret really was reached inside the last 90s,
      // just not by this press. See the KNOWN GAP note on `postSyncNow` — this
      // outcome can still mean "yesterday" was never pulled, and there is nothing
      // this screen can do about that without a backend change.
      return { tone: 'good', text: 'ALREADY SYNCED RECENTLY ✦ NOTHING NEW TO FETCH YET' }
    case 'synced':
      return { tone: 'good', text: '✓ SYNCED' }
    case 'sync_failed':
      return { tone: 'bad', text: `✕ SYNC FAILED ✦ ${state.error}` }
    case 'relink_required':
      return {
        tone: 'bad',
        text: '✕ FATSECRET NEEDS RECONNECTING ✦ PRESS RECONNECT FATSECRET BELOW',
      }
    case 'unconfigured':
      return { tone: 'bad', text: "✕ FATSECRET ISN'T CONFIGURED ON THIS SERVER" }
    case 'poll_timeout':
      return { tone: 'bad', text: 'STILL SYNCING ✦ CHECK BACK IN A MOMENT' }
    case 'error':
      return { tone: 'bad', text: `✕ ${state.message}` }
  }
}

/** How often SOURCES.CFG re-checks `/admin/sync/status` after `/admin/sync/now`
 *  answers `started` (or after catching `sync_in_progress`, which means someone
 *  else's run is doing the same work). `/admin/sync/now`'s own docstring puts a real
 *  run at up to 90s — three serial FatSecret calls, 30s timeout each — so the budget
 *  below is set a little past that: a run finishing at second 89 must not be reported
 *  as "still going" by a loop that gave up at 90. */
export const SYNC_POLL_INTERVAL_MS = 3000
export const SYNC_POLL_MAX_ATTEMPTS = 32

/** Resolves after `ms`, or immediately if `signal` aborts first — so a poll loop
 *  started by a component that then unmounts does not leak a live timer. */
function sleep(ms: number, signal: AbortSignal): Promise<void> {
  return new Promise((resolve) => {
    const id = window.setTimeout(resolve, ms)
    signal.addEventListener(
      'abort',
      () => {
        window.clearTimeout(id)
        resolve()
      },
      { once: true },
    )
  })
}

/* ── FatSecret link ──────────────────────────────────────────────────────── */

/** Which connect control SOURCES.CFG offers, or none. */
export type FatSecretLinkAction = 'connect' | 'reconnect' | null

/**
 * Whether to offer a link button, and which one.
 *
 * A pure helper rather than a condition inline in the JSX, because all four cases are
 * easy to get quietly wrong and none of them is visible by reading markup:
 *
 * - **no server credentials → nothing at all.** Not a disabled button: there is nothing
 *   the person holding the phone can do about a missing `MILO_FATSECRET_CONSUMER_KEY`,
 *   and a control that visibly does nothing is worse than no control.
 * - **not linked → Connect.**
 * - **linked but `relink_required` → Reconnect.** This is the case the whole feature
 *   exists for: FatSecret revoked upstream, the stored row still exists, and until now
 *   the only remedy was an SSH session.
 * - **linked and healthy → nothing.** Re-linking a working account is not a thing anyone
 *   needs to be invited to do.
 *
 * `!linked` is tested before `relink_required` because the server sets the latter true
 * whenever the former is false, and "Connect" is the honest word for a first link.
 */
/**
 * The error this screen should report as *this run's* failure, or null.
 *
 * `last_sync_error` alone is the wrong question and used to be asked anyway. The server
 * clears that column only on a success from the same origin that wrote it — the nightly
 * sweep, `/admin/sync/now`, or the MCP read-path guard — which is exactly right for
 * diagnosing the sweep and exactly wrong here. Without this, pressing "Sync now" the
 * morning after a failed 21:00 sweep rendered `sync_failed` on a sync that had just
 * succeeded, with a message about a run the user never started.
 *
 * `last_sync_error_current` is the server's own answer to "has a later success
 * superseded this", computed from the same timestamps rather than re-derived here so
 * the two surfaces cannot drift. A server older than migration 0007 does not send the
 * field, and `undefined` falls back to the old behaviour rather than silently hiding a
 * real failure.
 */
export function syncErrorToReport(sync: SyncStatus): string | null {
  if (!sync.last_sync_error) return null
  if (sync.last_sync_error_current === false) return null
  return sync.last_sync_error
}

export function fatsecretLinkAction(sync: SyncStatus): FatSecretLinkAction {
  if (!sync.consumer_configured) return null
  if (!sync.linked) return 'connect'
  if (sync.relink_required) return 'reconnect'
  return null
}

export const FATSECRET_LINK_LABEL: Record<'connect' | 'reconnect', string> = {
  connect: 'Connect FatSecret',
  reconnect: 'Reconnect FatSecret',
}

/**
 * One sentence for a `/start` that never got as far as sending the user to FatSecret.
 *
 * Its own mapper rather than a call to `errorMessage`, and for a specific reason:
 * `errorMessage` folds 401/403/503 into "Not signed in", which is right for a read that
 * failed a gate and wrong for every 503 this route emits — those mean the SERVER has no
 * FatSecret consumer pair or no usable callback URL, and telling the user to sign in
 * again sends them to do the one thing that cannot help. `syncNowLine` already keeps its
 * own 503 wording (`unconfigured`) for exactly this reason; this is the same decision.
 */
export function linkStartLine(error: unknown): SyncNowLine {
  if (error instanceof ApiError) {
    if (error.status === 503) {
      return { tone: 'bad', text: "✕ FATSECRET ISN'T CONFIGURED ON THIS SERVER" }
    }
    if (error.status === 502) {
      // /start asserts `oauth_callback_confirmed=true` and 502s otherwise, so this also
      // covers "FatSecret stopped accepting our callback" — which is precisely the
      // failure that would otherwise degrade silently to a dead-end PIN flow.
      return { tone: 'bad', text: "✕ COULDN'T START ✦ FATSECRET DIDN'T ACCEPT THE REQUEST" }
    }
    if (error.status === 401 || error.status === 403) {
      return { tone: 'bad', text: '✕ YOUR SESSION HAS EXPIRED ✦ SIGN IN AGAIN' }
    }
  }
  return { tone: 'bad', text: `✕ ${errorMessage(error)}` }
}

/** What `/auth/fatsecret/callback` redirected back with. */
export type LinkOutcome =
  | { ok: true }
  | { ok: false; reason: 'expired' | 'denied' | 'rejected' | 'upstream' | 'unknown' }

const LINK_REASONS = ['expired', 'denied', 'rejected', 'upstream'] as const

/**
 * Read the link outcome out of a `?link=…` query string.
 *
 * The callback redirects to `/` with a query rather than to a path of its own, and that
 * is a Caddy constraint rather than a preference: the SPA's static handler carries the
 * allowlist `/ /index.html /callback` and Caddy's `path` matcher is exact without a
 * wildcard, so a `/link-result` would fall through to the API and 404.
 *
 * **This only picks which sentence to show.** Whether the account is actually linked
 * comes from `GET /admin/sync/status`, which the panel reloads on mount — so someone who
 * hand-edits `?link=fatsecret_ok` into the URL sees one optimistic sentence and a pill
 * that still says NOT LINKED, because the pill is computed from server state.
 */
export function parseLinkOutcome(search: string): LinkOutcome | null {
  const params = new URLSearchParams(search)
  const link = params.get('link')
  if (link === 'fatsecret_ok') return { ok: true }
  if (link !== 'fatsecret_failed') return null
  const reason = params.get('reason') ?? ''
  return {
    ok: false,
    reason: (LINK_REASONS as readonly string[]).includes(reason)
      ? (reason as Exclude<LinkOutcome & { ok: false }, never>['reason'])
      : 'unknown',
  }
}

/* Namespaced like the auth keys in `auth/storage.ts`, and stored for the same reason
   the PKCE verifier is: it has to survive a full-page redirect to Cardea and back. */
const LINK_OUTCOME_KEY = 'milo.link.fatsecret_outcome'

/* sessionStorage can throw rather than merely be empty — Safari in private browsing has
   historically thrown on write, and an embedded context can have storage blocked
   outright. Both directions degrade to "the sentence is lost", which costs the user a
   confirmation line they can also read off the pill. Taking the screen down instead
   would not be a trade. */
function readStash(): string | null {
  try {
    return window.sessionStorage.getItem(LINK_OUTCOME_KEY)
  } catch {
    return null
  }
}

function writeStash(value: string | null): void {
  try {
    if (value === null) window.sessionStorage.removeItem(LINK_OUTCOME_KEY)
    else window.sessionStorage.setItem(LINK_OUTCOME_KEY, value)
  } catch {
    /* Storage unavailable: the outcome simply will not survive a sign-in. */
  }
}

/**
 * The link outcome for THIS page load — from the query if it is there, from the stash
 * if a sign-in ate it.
 *
 * **Why a stash at all.** Milo's access token lives in JS memory and is never
 * persisted, so a user returning from FatSecret needs a refresh round-trip before the
 * SPA will render anything signed-in. If the refresh token has also lapsed, `App`
 * renders `SignIn` and the document navigates to Cardea — which destroys every scrap of
 * React state *and* replaces the URL. Without this, the user completes the ceremony,
 * signs in, and is told nothing about it.
 *
 * **Deliberately not read-and-clear**, unlike `takePending()` in `auth/storage.ts`.
 * Two callers read this — `App` to decide which tab opens, `Settings` to say what
 * happened — and consuming it in whichever ran first would starve the other. It is
 * cleared by the screen that DISPLAYS it, next to the URL scrub that already exists for
 * the same reason. That also keeps this function repeatable within one page load, so
 * neither caller depends on the other's timing.
 *
 * **Nothing here is trusted.** The outcome only picks which sentence to show; whether
 * the account is actually linked comes from `GET /admin/sync/status`. A hand-written
 * stash buys the same nothing a hand-edited `?link=fatsecret_ok` does — one optimistic
 * sentence over a pill that still says NOT LINKED.
 */
export function resolveLinkOutcome(search: string): LinkOutcome | null {
  const fromQuery = parseLinkOutcome(search)
  if (fromQuery) {
    writeStash(JSON.stringify(fromQuery))
    return fromQuery
  }
  const stashed = readStash()
  if (!stashed) return null
  try {
    const parsed: unknown = JSON.parse(stashed)
    if (parsed && typeof parsed === 'object' && typeof (parsed as LinkOutcome).ok === 'boolean') {
      return parsed as LinkOutcome
    }
    return null
  } catch {
    return null
  }
}

/** Forget the stashed outcome. Called once the sentence has actually been rendered. */
export function clearStoredLinkOutcome(): void {
  writeStash(null)
}

/**
 * One sentence per outcome. `expired` deliberately covers three server-side cases that
 * are reported identically — a replayed callback, a token that was never issued, and a
 * browser that presented no correlation cookie — because distinguishing them in the
 * response would confirm to an attacker that their attempt reached a real pending row.
 * So the wording has to be true of all three, which "didn't complete in time" is.
 */
export function linkOutcomeLine(outcome: LinkOutcome | null): SyncNowLine | null {
  if (!outcome) return null
  if (outcome.ok) return { tone: 'good', text: '✓ FATSECRET CONNECTED' }
  switch (outcome.reason) {
    case 'denied':
      return { tone: 'bad', text: "✕ NOT CONNECTED ✦ YOU DIDN'T APPROVE THE REQUEST" }
    case 'expired':
      return {
        tone: 'bad',
        text: "✕ NOT CONNECTED ✦ THAT ATTEMPT DIDN'T COMPLETE IN TIME — TRY AGAIN",
      }
    case 'rejected':
      return { tone: 'bad', text: '✕ NOT CONNECTED ✦ FATSECRET REFUSED THE APPROVAL — TRY AGAIN' }
    case 'upstream':
      return { tone: 'bad', text: "✕ NOT CONNECTED ✦ COULDN'T REACH FATSECRET — TRY AGAIN" }
    default:
      return { tone: 'bad', text: '✕ NOT CONNECTED ✦ TRY AGAIN' }
  }
}

/* ── Export ──────────────────────────────────────────────────────────────── */

/** `_EXPORT_TABLES` in `src/milo/routers/read.py`, in a sensible reading order.
 *  Exported so the test can hold it to the server's list: a table missing here is
 *  silently missing from "export everything", which is the wrong export of a
 *  health record. */
export const EXPORT_TABLES: readonly ExportTable[] = [
  'measurements',
  'nutrition',
  'vitals',
  'vital_devices',
  'vital_metrics',
  'vital_metric_aliases',
  'vital_metric_prefs',
  'journal',
  'plan_items',
  'goals',
]

/**
 * Data rows in one CSV — every line but the header, blank lines ignored.
 *
 * Quote-aware on purpose. A journal entry is free text typed into a textarea, so
 * it can and does contain newlines, and the exporter quotes them rather than
 * escaping them. Counting `\n` would report a paragraph as several records.
 */
export function countCsvRows(csv: string): number {
  let rows = 0
  let inQuotes = false
  let onARow = false

  for (let i = 0; i < csv.length; i++) {
    const ch = csv[i]
    if (inQuotes) {
      if (ch === '"') {
        // `""` inside a quoted field is one literal quote, not the end of it.
        if (csv[i + 1] === '"') i++
        else inQuotes = false
      }
      onARow = true
      continue
    }
    if (ch === '"') {
      inQuotes = true
      onARow = true
    } else if (ch === '\n') {
      if (onARow) rows++
      onARow = false
    } else if (ch !== '\r') {
      onARow = true
    }
  }
  if (onARow) rows++

  // The first row is the header. An empty file has none, hence the clamp.
  return Math.max(0, rows - 1)
}

function stamp(now: Date): string {
  return `${now.getFullYear()}-${twoDigit(now.getMonth() + 1)}-${twoDigit(now.getDate())}`
}

/* ── Loading ─────────────────────────────────────────────────────────────── */

type Load<T> =
  | { status: 'loading' }
  | { status: 'ready'; data: T }
  | { status: 'error'; message: string }

function isAbort(error: unknown): boolean {
  return error instanceof DOMException && error.name === 'AbortError'
}

/** Window shell + body, so windows sharing a grid row stretch to equal height. */
const FILL = {
  style: {
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    boxSizing: 'border-box',
  },
  bodyStyle: { flex: 1 },
} as const

/* The dev shim, read the same way `App.tsx` reads it and for the same reason:
   under VITE_DEV_USER there is no session at all — no token, no claims, no
   sign-out to perform — but there IS a name the shell is displaying, and
   ACCOUNT.CFG showing "—" beside a header pill saying REHMAN would be worse than
   showing the shim for what it is. Read only under DEV, so it cannot reach a
   production build. */
const DEV_USER = import.meta.env.DEV
  ? (import.meta.env.VITE_DEV_USER as string | undefined)
  : undefined

/* ── Screen ──────────────────────────────────────────────────────────────── */

export function Settings() {
  const { status, user, signOut } = useAuth()

  const [goals, setGoals] = useState<Load<Record<string, string>>>({ status: 'loading' })
  const [sync, setSync] = useState<Load<SyncStatus>>({ status: 'loading' })

  // Goal fields. Seeded from the read, then owned by the user until saved.
  const [targetWeight, setTargetWeight] = useState('')
  const [weeklyRate, setWeeklyRate] = useState('')
  const [shown, setShown] = useState<Set<string>>(new Set(ALL_MEASUREMENTS))

  const [goalNote, setGoalNote] = useState<string | null>(null)
  const [goalError, setGoalError] = useState<string | null>(null)

  const [exporting, setExporting] = useState(false)
  const [exported, setExported] = useState<number | null>(null)
  const [exportError, setExportError] = useState<string | null>(null)

  const [syncNow, setSyncNow] = useState<SyncNowState>({ phase: 'idle' })

  /* The `?link=…` the FatSecret callback redirected back with.

     Read in a lazy initialiser — during RENDER, before any effect can run — so that the
     scrub below cannot race it. App.tsx reads the same query the same way to decide
     which tab opens; both reads happen before anything rewrites the URL, so neither
     depends on the other's timing. */
  const [linkOutcome] = useState<LinkOutcome | null>(() =>
    resolveLinkOutcome(window.location.search),
  )
  const [linking, setLinking] = useState(false)
  const [linkError, setLinkError] = useState<SyncNowLine | null>(null)

  /* Drop the query once it has been read, and the stash with it. Same technique and
     same reason as the sign-in callback's own scrub in `auth/session.ts`: a URL
     carrying flow state must not survive a reload or a bookmark, or refreshing the page
     would re-show an outcome that is no longer news.

     This is the ONLY place the stash is cleared, and it is here rather than inside
     `resolveLinkOutcome` because this screen is what displays the sentence. App reads
     the same value to pick the tab, and a read-and-clear there would consume the
     outcome before the screen it was opened for could show it. */
  useEffect(() => {
    if (linkOutcome) {
      window.history.replaceState(null, '', '/')
      clearStoredLinkOutcome()
    }
  }, [linkOutcome])
  /* Cancels an in-flight request or poll loop when a new press starts, and again on
     unmount — otherwise a poll started before navigating away keeps calling
     `getSyncStatus` and writing to state on an unmounted component. */
  const syncAbort = useRef<AbortController | null>(null)
  useEffect(() => () => syncAbort.current?.abort(), [])

  /* The written map, kept alongside the fields so a blur that changed nothing
     does not POST. It is a ref rather than state because it is read inside the
     save handlers and never rendered — as state it would be a stale closure on
     exactly the path that matters. */
  const written = useRef<Record<string, string>>({})

  /* One slot per goal key currently being written, holding the newest value
     that key should end up at. Empty means nothing is in flight. See
     `writeGoal` for what the slot is for. */
  const writing = useRef(new Map<string, { latest: string }>())
  /* Did anything in the current burst of writes fail? A ref, because it is read
     after an await by a sibling write that may have started before the failure
     — as state it would be the value from before the error. */
  const writeFailed = useRef(false)

  const loadGoals = useCallback(async (signal?: AbortSignal) => {
    setGoals({ status: 'loading' })
    try {
      const res = await getGoals(signal)
      written.current = res.goals
      setGoals({ status: 'ready', data: res.goals })
      setTargetWeight(res.goals[GOAL_TARGET_WEIGHT] ?? '')
      setWeeklyRate(res.goals[GOAL_WEEKLY_RATE] ?? '')
      setShown(parseMeasurementPref(res.goals[GOAL_LOG_MEASUREMENTS]))
    } catch (error) {
      if (isAbort(error)) return
      setGoals({ status: 'error', message: errorMessage(error) })
    }
  }, [])

  const loadSync = useCallback(async (signal?: AbortSignal) => {
    setSync({ status: 'loading' })
    try {
      setSync({ status: 'ready', data: await getSyncStatus(signal) })
    } catch (error) {
      if (isAbort(error)) return
      setSync({ status: 'error', message: errorMessage(error) })
    }
  }, [])

  useEffect(() => {
    const controller = new AbortController()
    void loadGoals(controller.signal)
    void loadSync(controller.signal)
    return () => controller.abort()
  }, [loadGoals, loadSync])

  /* ── Sync now ──────────────────────────────────────────────────────────── */

  /**
   * Poll `/admin/sync/status` until `sync_in_progress` clears, or give up after
   * `SYNC_POLL_MAX_ATTEMPTS`. Reached from two places: after `started` (202), and
   * after catching `sync_in_progress` (409) — someone else's run is doing the exact
   * same fetch this press would have started, so watching it is the honest answer
   * rather than a second no-op request or a bare "try again later".
   *
   * Once it clears, the fresh `SyncStatus` is written into `sync` too — the same read
   * that feeds the FATSECRET pill — so the pill's timestamp updates without a second,
   * separate reload. `last_sync_error` on that same read is how a failure that happened
   * in the background (this tab's own run, or the one this tab was watching) is told
   * apart from a success: the endpoint's 202 response carries no result, only "started"
   * — counts aren't available here the way they are from the synchronous `/admin/sync`
   * sweep, and inventing one would be a lie.
   */
  const pollUntilDone = useCallback(async (signal: AbortSignal) => {
    for (let attempt = 0; attempt < SYNC_POLL_MAX_ATTEMPTS; attempt++) {
      await sleep(SYNC_POLL_INTERVAL_MS, signal)
      if (signal.aborted) return
      let status: SyncStatus
      try {
        status = await getSyncStatus(signal)
      } catch (error) {
        if (isAbort(error)) return
        // A read hiccup mid-poll is not the sync's failure — keep trying rather
        // than reporting a false negative from an unrelated network blip.
        continue
      }
      if (signal.aborted) return
      if (!status.sync_in_progress) {
        setSync({ status: 'ready', data: status })
        setSyncNow(
          syncErrorToReport(status) !== null
            ? { phase: 'sync_failed', error: status.last_sync_error as string }
            : { phase: 'synced' },
        )
        return
      }
    }
    setSyncNow({ phase: 'poll_timeout' })
  }, [])

  async function syncNowPressed() {
    if (syncNow.phase === 'requesting' || syncNow.phase === 'polling') return
    syncAbort.current?.abort()
    const controller = new AbortController()
    syncAbort.current = controller

    /* A failed Connect is news from a previous press, and `linkError` outranks
       everything else in `syncLine`. Left standing it swallows this run's whole
       vocabulary — SYNCING…, ✓ SYNCED, and any failure — while both buttons stay
       enabled, so the user presses Sync now and watches an unrelated error not change. */
    setLinkError(null)
    setSyncNow({ phase: 'requesting' })
    try {
      const result = await postSyncNow(controller.signal)
      if (controller.signal.aborted) return
      if (result.status === 'recently_synced') {
        setSyncNow({ phase: 'recently_synced' })
        return
      }
      // status === 'started': the fetch is running in the background now.
      setSyncNow({ phase: 'polling' })
      await pollUntilDone(controller.signal)
    } catch (error) {
      if (isAbort(error)) return
      if (error instanceof ApiError && error.status === 409 && error.detail === 'relink_required') {
        setSyncNow({ phase: 'relink_required' })
      } else if (error instanceof ApiError && error.status === 409 && error.detail === 'sync_in_progress') {
        setSyncNow({ phase: 'polling' })
        await pollUntilDone(controller.signal)
      } else if (error instanceof ApiError && error.status === 503) {
        setSyncNow({ phase: 'unconfigured' })
      } else {
        setSyncNow({ phase: 'error', message: errorMessage(error) })
      }
    }
  }

  /* ── FatSecret link ────────────────────────────────────────────────────── */

  /**
   * Start the browser link ceremony and hand the tab to FatSecret.
   *
   * **`location.assign`, in the CURRENT tab — never `window.open`.** The authorize URL
   * is only known after awaiting `/start`, so a `window.open` here is not synchronous
   * with the user's click and iOS Safari blocks it as an unsolicited popup — on exactly
   * the phone browser this feature exists to serve, and with no error the user could
   * report: they would press Connect and see nothing happen at all. A top-level redirect
   * has no such rule.
   *
   * The cost is that this screen unmounts, which is acceptable here and was checked
   * rather than assumed against the "never lose typed input" rule: the goal fields commit
   * on blur, and pressing this button blurs whichever of them had focus. The callback
   * returns to `/`, where App reopens SETTINGS and `?link=` states the outcome.
   *
   * `setLinking(false)` is deliberately absent from the success path — the document is
   * navigating away, and clearing the flag would flicker the button back to idle in the
   * moment before it goes.
   */
  async function connectPressed() {
    if (linking) return
    setLinking(true)
    setLinkError(null)
    try {
      const { authorize_url } = await postFatSecretLinkStart()
      window.location.assign(authorize_url)
    } catch (error) {
      setLinkError(linkStartLine(error))
      setLinking(false)
    }
  }

  /* ── Writes ────────────────────────────────────────────────────────────── */

  /**
   * Save one goal. Never drops a write.
   *
   * This panel produces bursts: five checkboxes sit next to each other and get
   * ticked in one movement, and blurring one number straight into the other
   * fires two saves before either POST returns. A single in-flight flag turns
   * every write after the first into a silent no-op — and because the checkbox
   * has already moved and the line already says SAVING…/SAVED, the UI reports a
   * success for a request it never sent. The setting is only discovered missing
   * on the next reload, with nothing anywhere to say why. Never again.
   *
   * ACROSS KEYS: fully concurrent. `POST /goals` upserts on (user, key), so two
   * different keys cannot conflict and there is nothing to serialise.
   *
   * WITHIN ONE KEY: last write wins, by coalescing rather than queueing. A
   * change made while that key's POST is in flight replaces the value in the
   * key's slot; the loop below re-reads the slot when the POST returns and
   * sends again if it moved. So the final value always reaches the server, but
   * a user who ticks and unticks the same box four times causes at most two
   * POSTs rather than four. Queueing all four would send three values that the
   * store immediately overwrites — same destination, more requests, and a
   * longer window in which the server disagrees with the screen.
   */
  async function writeGoal(key: string, value: string) {
    const running = writing.current.get(key)
    if (running) {
      // Coalesce onto the loop already running for this key. It re-reads the
      // slot after every POST, so this value is sent — not dropped.
      running.latest = value
      return
    }

    if (writing.current.size === 0) {
      // First write of a burst: its outcome is what the goals line will report.
      writeFailed.current = false
      setGoalError(null)
    }
    const slot = { latest: value }
    writing.current.set(key, slot)
    setGoalNote('SAVING…')

    try {
      let sent: string | null = null
      while (sent !== slot.latest) {
        sent = slot.latest
        await postGoals({ key, value: sent })
        written.current = { ...written.current, [key]: sent }
      }
    } catch (error) {
      writeFailed.current = true
      setGoalError(errorMessage(error))
    } finally {
      writing.current.delete(key)
      // SAVED only once every key in the burst has landed, and only if none of
      // them failed — otherwise a fast success would paint over a sibling's
      // error message.
      if (writing.current.size === 0) {
        setGoalNote(writeFailed.current ? null : 'SAVED')
      }
    }
  }

  /* Numeric goals save on blur (and on Enter, which blurs). There is no Save
     button because the design does not draw one and the two fields are the whole
     panel — but "it saved when I clicked away" is invisible, so the mono line
     under them says which of the three things just happened. */
  function commitNumber(key: string, raw: string) {
    const trimmed = raw.trim()
    // Blank is "I have not decided", not "delete my goal". The store has no
    // delete, and writing "" would leave the coach reading an empty string.
    if (!trimmed) return
    const parsed = Number.parseFloat(trimmed)
    if (!Number.isFinite(parsed)) {
      setGoalError(`${key === GOAL_TARGET_WEIGHT ? 'Target weight' : 'Weekly rate'}: use a number.`)
      return
    }
    const before = written.current[key]
    if (before !== undefined && Number.parseFloat(before) === parsed) return
    void writeGoal(key, trimmed)
  }

  function toggleMeasurement(metric: string, on: boolean) {
    const next = new Set(shown)
    if (on) next.add(metric)
    else next.delete(metric)
    // Optimistic: the checkbox is the user's own click and must move under the
    // finger. A failed write is reported on the goals line, and a reload shows
    // the server's answer rather than this one.
    setShown(next)
    void writeGoal(GOAL_LOG_MEASUREMENTS, formatMeasurementPref(next))
  }

  async function exportEverything() {
    if (exporting) return
    setExporting(true)
    setExportError(null)
    try {
      /* "Everything you ever logged, in one file." The route serves one table
         per request, so the five are fetched and concatenated here, each under a
         `# TABLE:` line. One download, and a file whose sections are obvious —
         rather than five downloads, which every browser interrupts with a
         "allow multiple downloads?" prompt. */
      const parts: string[] = []
      let total = 0
      for (const table of EXPORT_TABLES) {
        const { blob } = await fetchExportCsv(table)
        const text = await blob.text()
        total += countCsvRows(text)
        parts.push(`# TABLE: ${table}\n${text.replace(/\s+$/, '')}\n`)
      }

      const file = new Blob([parts.join('\n')], { type: 'text/csv;charset=utf-8' })
      const url = URL.createObjectURL(file)
      try {
        const anchor = document.createElement('a')
        anchor.href = url
        anchor.download = `milo-export-${stamp(new Date())}.csv`
        // Firefox ignores a click on an anchor that is not in the document.
        document.body.appendChild(anchor)
        anchor.click()
        anchor.remove()
      } finally {
        // Revoked on the next task, not synchronously: the browser reads the
        // object URL after click() returns, and revoking first cancels the
        // download it was asked to start.
        window.setTimeout(() => URL.revokeObjectURL(url), 0)
      }
      setExported(total)
    } catch (error) {
      setExportError(errorMessage(error))
    } finally {
      setExporting(false)
    }
  }

  /* ── Derived ───────────────────────────────────────────────────────────── */

  const signedIn = status === 'signed-in'
  const name = user ?? DEV_USER ?? 'Unknown'
  const initial = name.slice(0, 1)

  const goalsBusy = goals.status === 'loading'
  const readError =
    goals.status === 'error'
      ? goals.message
      : sync.status === 'error'
        ? sync.message
        : null

  let goalsLine: string
  if (goalError) goalsLine = goalError
  else if (goals.status === 'loading') goalsLine = 'LOADING…'
  else if (goals.status === 'error') goalsLine = 'COULD NOT READ YOUR GOALS'
  else if (goalNote) goalsLine = goalNote
  else if (!written.current[GOAL_TARGET_WEIGHT] && !written.current[GOAL_WEEKLY_RATE])
    goalsLine = 'NOT SET YET ✦ SAVES WHEN YOU CLICK AWAY'
  else goalsLine = 'SAVES WHEN YOU CLICK AWAY'

  let exportLine: string
  if (exporting) exportLine = 'EXPORTING…'
  else if (exported !== null)
    exportLine = `CSV ✦ ${exported.toLocaleString('en-US')} RECORDS`
  else exportLine = `CSV ✦ ${EXPORT_TABLES.length} TABLES`

  const syncBusy = syncNow.phase === 'requesting' || syncNow.phase === 'polling'
  /* One flash line, three possible authors, in priority order: a link attempt that just
     failed, then whatever the Sync-now button is doing, then the outcome this page load
     arrived carrying. Newest news wins — the `?link=` sentence is from before the page
     rendered, so anything the user has done since is more relevant. */
  const syncLine: SyncNowLine | null =
    linkError ?? syncNowLine(syncNow) ?? linkOutcomeLine(linkOutcome)
  const linkAction = sync.status === 'ready' ? fatsecretLinkAction(sync.data) : null

  return (
    <>
      {readError ? (
        <Alert tone="danger" role="alert" title="Can’t read your settings.">
          {readError}
        </Alert>
      ) : null}

      <div className="milo-grid">
        <Window title="ACCOUNT.CFG" shadow="m" style={FILL.style} bodyStyle={FILL.bodyStyle}>
          <div className="milo-panel">
            <div className="milo-account">
              <div className="milo-avatar" aria-hidden="true">
                {initial}
              </div>
              <div>
                <div className="milo-account-name">{name}</div>
                <div className="milo-account-via">
                  {signedIn ? 'VIA CARDEA / POCKETID' : 'VIA VITE_DEV_USER / NO SESSION'}
                </div>
              </div>
            </div>

            <div className="milo-scopes">
              {signedIn
                ? 'SCOPES: MILO:READ ✦ MILO:WRITE'
                : 'SCOPES: DEV BEARER ✦ NOT A SESSION'}
            </div>

            {/* `Switch user ⌁` from the prototype is deliberately absent. It was a
                demo affordance for a two-account mock: Milo decides who you are
                from the credential you presented, and there is no second
                credential sitting in the browser to switch to. A picker with
                nothing behind it could only lie about who it had signed in, so
                the honest control is sign out and sign in as someone else.

                Sign-out is likewise hidden under the dev shim rather than shown
                disabled — there is no session for it to end, and a button that
                visibly does nothing is worse than no button. */}
            {signedIn ? (
              <div className="milo-button-row">
                <Button variant="dark" size="sm" iconRight="×" onClick={signOut}>
                  Sign out
                </Button>
              </div>
            ) : (
              <div className="milo-copy milo-copy--muted">
                Signed in by the dev shim, so there is no session to end. Unset
                VITE_DEV_USER to use the real sign-in.
              </div>
            )}
          </div>
        </Window>

        <Window title="GOALS.CFG" shadow="m" style={FILL.style} bodyStyle={FILL.bodyStyle}>
          <div className="milo-panel">
            <Input
              label="Target weight (lb)"
              type="number"
              inputMode="decimal"
              step="0.1"
              min="0"
              placeholder="Not set"
              helper="Drawn as the pink line on the trend chart."
              disabled={goalsBusy}
              value={targetWeight}
              onChange={(e) => setTargetWeight(e.target.value)}
              onBlur={() => commitNumber(GOAL_TARGET_WEIGHT, targetWeight)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') e.currentTarget.blur()
              }}
            />
            <Input
              label="Weekly rate (lb/wk)"
              type="number"
              inputMode="decimal"
              step="0.1"
              placeholder="Not set"
              helper="The pace the coach holds you to."
              disabled={goalsBusy}
              value={weeklyRate}
              onChange={(e) => setWeeklyRate(e.target.value)}
              onBlur={() => commitNumber(GOAL_WEEKLY_RATE, weeklyRate)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') e.currentTarget.blur()
              }}
            />
            <div
              className={goalError ? 'milo-flash milo-flash--bad' : 'milo-note milo-note--sm'}
              role="status"
            >
              {goalsLine}
            </div>
          </div>
        </Window>

        <Window title="UNITS.CFG" shadow="m" style={FILL.style} bodyStyle={FILL.bodyStyle}>
          {/* Spans, not buttons. v1 is locked to lb and inches — every write in
              this app sends `unit: 'lb'` or `'in'` — so these state which unit is
              in force. Rendering them as controls would promise a conversion
              that does not exist. */}
          <div className="milo-panel">
            <div className="milo-unit-row">
              <span className="milo-unit-label">WEIGHT</span>
              <span className="milo-unit-chip milo-unit-chip--on">LB ✓</span>
              <span className="milo-unit-chip milo-unit-chip--off">KG</span>
            </div>
            <div className="milo-unit-row">
              <span className="milo-unit-label">LENGTH</span>
              <span className="milo-unit-chip milo-unit-chip--on">IN ✓</span>
              <span className="milo-unit-chip milo-unit-chip--off">CM</span>
            </div>
          </div>
        </Window>

        <Window
          title="MEASUREMENTS.CFG"
          shadow="m"
          style={FILL.style}
          bodyStyle={FILL.bodyStyle}
        >
          <div className="milo-panel milo-panel--10">
            <div className="milo-note milo-note--sm">SHOWN ON THE LOG SCREEN</div>
            {MEASUREMENT_FIELDS.map((field) => (
              <Checkbox
                key={field.metric}
                label={field.label}
                checked={shown.has(field.metric)}
                /* Locked until the read lands, unlike the two goal fields above.
                   Those write exactly what was typed, so an unknown prior value
                   is irrelevant; this one writes the whole list, derived from
                   the boxes on screen. If the read failed, those boxes are the
                   all-five default rather than the stored preference — and one
                   click would overwrite a real setting with a guess. */
                disabled={goals.status !== 'ready'}
                onChange={(e) => toggleMeasurement(field.metric, e.target.checked)}
              />
            ))}
          </div>
        </Window>

        <Window title="SOURCES.CFG" shadow="m" style={FILL.style} bodyStyle={FILL.bodyStyle}>
          <div className="milo-panel milo-panel--12">
            <div className="milo-source-row">
              <span className="milo-source-name">MANUAL</span>
              {/* Not a status read — a property of the backend. `SOURCE_PRIORITY`
                  puts manual above every synced source in every deduped read. */}
              <Pill color="yellow">ALWAYS WINS</Pill>
            </div>
            <div className="milo-source-row">
              <span className="milo-source-name">FATSECRET</span>
              {sync.status === 'loading' ? (
                <Pill color="paper">CHECKING…</Pill>
              ) : sync.status === 'error' ? (
                <Pill color="paper">UNKNOWN</Pill>
              ) : (
                (() => {
                  const spec = fatsecretPill(sync.data, new Date())
                  return <Pill color={spec.color}>{spec.label}</Pill>
                })()
              )}
            </div>
            <div className="milo-source-row">
              <span className="milo-source-name">AI COACH / MCP</span>
              {/* CONNECTED is not a probe result. `/mcp` is mounted by the same
                  FastAPI app these panels just read from, so if anything on this
                  screen loaded, the coach's endpoint is being served. */}
              <Pill color="purple">CONNECTED</Pill>
            </div>
            <p className="milo-copy milo-copy--muted">
              Manual entries beat synced ones for the same date and metric. The
              coach reads everything over MCP.
            </p>
            {/* Escape hatch for "I know something changed, fetch it now" — the
                nightly sweep and the on-demand pull on page load both happen
                without anyone pressing anything. `POST /admin/sync/now` answers
                honestly rather than optimistically, so every one of its outcomes
                (and the poll that follows `started`) gets its own line below,
                via `syncNowLine`. Rendered only once the read this button acts
                on has itself loaded — pressing it before that is not something
                a person can do, since there is nothing to click yet. */}
            {sync.status === 'ready' ? (
              <div className="milo-button-row">
                <Button
                  variant="secondary"
                  size="sm"
                  disabled={syncBusy}
                  aria-busy={syncBusy}
                  onClick={() => void syncNowPressed()}
                >
                  {syncBusy ? 'Syncing…' : 'Sync now'}
                </Button>
                {/* The self-serve link ceremony, deliberately sitting beside the
                    button whose `relink_required` outcome used to send people to a
                    runbook and a terminal. Offered only when there is something a
                    person can actually do — see `fatsecretLinkAction`. */}
                {linkAction ? (
                  <Button
                    variant="primary"
                    size="sm"
                    disabled={linking}
                    aria-busy={linking}
                    onClick={() => void connectPressed()}
                  >
                    {linking ? 'Opening FatSecret…' : FATSECRET_LINK_LABEL[linkAction]}
                  </Button>
                ) : null}
              </div>
            ) : null}
            {syncLine ? (
              <div
                className={syncLine.tone === 'bad' ? 'milo-flash milo-flash--bad' : 'milo-flash'}
                role={syncLine.tone === 'bad' ? 'alert' : 'status'}
              >
                {syncLine.text}
              </div>
            ) : null}
          </div>
        </Window>

        <Window title="EXPORT.CFG" shadow="m" style={FILL.style} bodyStyle={FILL.bodyStyle}>
          <div className="milo-panel milo-panel--12">
            <p className="milo-copy">
              Everything you ever logged, in one file. Your data, your bull.
            </p>
            <Button
              variant="secondary"
              iconRight="↓"
              disabled={exporting}
              aria-busy={exporting}
              onClick={() => void exportEverything()}
            >
              Export everything
            </Button>
            {exportError ? (
              <div className="milo-flash milo-flash--bad" role="alert">
                ✕ {exportError}
              </div>
            ) : null}
            {/* The prototype's `JSON + CSV ✦ 2,861 RECORDS` was sample data, and
                half of it was never true — the route serves CSV only. So the
                claim is cut to what is served, and the count is derived from the
                file that was actually produced rather than guessed at. Before an
                export there is no count to state, so it states the table count,
                which is knowable. */}
            <div className="milo-note milo-note--sm" role="status">
              {exportLine}
            </div>
          </div>
        </Window>
      </div>
    </>
  )
}

export default Settings
